import { NavLink, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const products = [
    { slug: "estamp", name: "Colecle eStamp", desc: "Digital stamping & verification" },
    { slug: "monitor", name: "Colecle Monitor", desc: "ICT infrastructure intelligence" },
    { slug: "procurement", name: "Colecle Procurement & Contracts", desc: "End-to-end lifecycle governance" },
    { slug: "rent", name: "Colecle Rent", desc: "Smart property & rental platform" },
    { slug: "events", name: "Colecle Events", desc: "Enterprise event governance" },
];

const links = [
    { to: "/products", label: "Products", hasDropdown: true },
    { to: "/solutions", label: "Solutions" },
    { to: "/pricing", label: "Pricing" },
    { to: "/company", label: "Company" },
    { to: "/support", label: "Support" },
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [productsOpen, setProductsOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled ? "bg-[#0B1930]/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
            }`}
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
                <Link to="/" data-testid="brand-link" className="flex items-center gap-2.5 group">
                    <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#E8B923] to-[#C99F0F] flex items-center justify-center text-navy font-serif text-lg font-bold">C</div>
                    <div className="flex flex-col leading-none">
                        <span className="text-white font-serif text-xl font-semibold tracking-tight">Colecle</span>
                        <span className="text-white/50 text-[10px] font-mono tracking-[0.2em] mt-0.5">INC</span>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    {links.map((l) =>
                        l.hasDropdown ? (
                            <div
                                key={l.to}
                                className="relative"
                                onMouseEnter={() => setProductsOpen(true)}
                                onMouseLeave={() => setProductsOpen(false)}
                            >
                                <button data-testid={`nav-${l.label.toLowerCase()}`} className="text-white/80 hover:text-white text-sm px-4 py-2 flex items-center gap-1 transition">
                                    {l.label}
                                    <ChevronDown className="w-3.5 h-3.5" />
                                </button>
                                <AnimatePresence>
                                    {productsOpen && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: 8 }}
                                            transition={{ duration: 0.18 }}
                                            className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-[420px]"
                                        >
                                            <div className="bg-[#0B1930] border border-white/10 rounded-xl shadow-2xl p-3">
                                                {products.map((p) => (
                                                    <Link
                                                        key={p.slug}
                                                        to={`/products/${p.slug}`}
                                                        data-testid={`nav-product-${p.slug}`}
                                                        className="flex items-start justify-between gap-4 rounded-lg px-3 py-3 hover:bg-white/5 transition group"
                                                    >
                                                        <div>
                                                            <div className="text-white text-sm font-medium">{p.name}</div>
                                                            <div className="text-white/50 text-xs mt-0.5">{p.desc}</div>
                                                        </div>
                                                        <ArrowUpRight className="w-4 h-4 text-white/30 group-hover:text-[#E8B923] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
                                                    </Link>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        ) : (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={`nav-${l.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `text-sm px-4 py-2 transition ${isActive ? "text-white" : "text-white/70 hover:text-white"}`
                                }
                            >
                                {l.label}
                            </NavLink>
                        )
                    )}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <Link to="/login" data-testid="nav-signin" className="text-white/70 hover:text-white text-sm">Sign in</Link>
                    <Link to="/request-demo" data-testid="nav-demo-cta" className="btn-gold text-sm">
                        Request Demo
                        <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>

                <button data-testid="mobile-menu-toggle" className="lg:hidden text-white p-2" onClick={() => setMobileOpen((v) => !v)}>
                    {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            <AnimatePresence>
                {mobileOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="lg:hidden bg-[#0B1930] border-t border-white/10 overflow-hidden"
                    >
                        <div className="px-6 py-6 space-y-3">
                            {links.map((l) => (
                                <NavLink key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="block text-white/80 py-2 text-base">{l.label}</NavLink>
                            ))}
                            <div className="pt-4 border-t border-white/10 space-y-3">
                                <Link to="/login" onClick={() => setMobileOpen(false)} className="block text-white/70">Sign in</Link>
                                <Link to="/request-demo" onClick={() => setMobileOpen(false)} className="btn-gold text-sm w-full justify-center">Request Demo</Link>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
